import { HourlyForecastTimelineData, HourlyBucketResponse, HourlyActualsResponse, ErrorInfo } from '../models/HourlyForecastTimelineData';
import { FogForecastResponse } from './FogForecastService';

export type NotificationIssue = {
  type: 'error' | 'warning' | 'info';
  title: string;
  message: string;
  id: string;
};

// Extended interfaces to include errors field
interface ExtendedAggregatedPredictions extends HourlyBucketResponse {
  errors?: ErrorInfo[];
}

interface ExtendedAggregatedActuals extends HourlyActualsResponse {
  errors?: ErrorInfo[];
}

interface ExtendedTimelineData extends HourlyForecastTimelineData {
  errors?: ErrorInfo[];
}

export class NotificationService {
  /**
   * Maps error codes to user-friendly messages
   */
  private static errorMessageMap: Record<string, string> = {
    // NZ forecast errors
    'MISSING_PREDICTIONS': 'Some forecast data is missing for this time period',
    'MISSING_ACTUALS': 'Some historical visibility data is missing for this time period',
    'EXCESSIVE_MISSING_PREDICTIONS': 'A significant amount of forecast data is missing. Forecast accuracy may be impacted',
    'EXCESSIVE_MISSING_ACTUALS': 'Recent historical visibility data is not available for some locations. Forecast accuracy may be impacted the longer this occurs',
    // UK METAR forecast errors
    'MISSING_PREDICTIONS_30MIN': 'Some forecast data is missing for this time period',
    'MISSING_PREDICTIONS_HOURLY': 'Some forecast data is missing for this time period',
    'EXCESSIVE_MISSING_PREDICTIONS_30MIN': 'A significant amount of forecast data is missing. Forecast accuracy may be impacted',
    'EXCESSIVE_MISSING_PREDICTIONS_HOURLY': 'A significant amount of forecast data is missing. Forecast accuracy may be impacted'
  };

  /**
   * Maps error codes to user-friendly titles
   */
  private static errorTitleMap: Record<string, string> = {
    // NZ forecast errors
    'MISSING_PREDICTIONS': 'Incomplete Predictions',
    'MISSING_ACTUALS': 'Incomplete Historical Data',
    'EXCESSIVE_MISSING_PREDICTIONS': 'Missing Prediction Data',
    'EXCESSIVE_MISSING_ACTUALS': 'Missing Historical Data',
    // UK METAR forecast errors
    'MISSING_PREDICTIONS_30MIN': 'Incomplete Predictions',
    'MISSING_PREDICTIONS_HOURLY': 'Incomplete Predictions',
    'EXCESSIVE_MISSING_PREDICTIONS_30MIN': 'Missing Prediction Data',
    'EXCESSIVE_MISSING_PREDICTIONS_HOURLY': 'Missing Prediction Data'
  };

  /**
   * Checks forecast data for issues that should be displayed to users
   */
  static checkForDataIssues(params: {
    timelineData: HourlyForecastTimelineData[];
    loading: boolean;
    hasCustomNow: boolean;
    error: string | null;
  }): NotificationIssue | null {
    const { timelineData, loading, hasCustomNow, error } = params;
    
    // Skip checks if using custom now parameter
    if (hasCustomNow) return null;
    
    // Skip if still loading initial data
    if (loading && timelineData.length === 0) return null;
    
    // Initialize an array to track detected issues
    const issues: NotificationIssue[] = [];
    
    // Check for empty data
    if (timelineData.length === 0 && !loading) {
      issues.push({
        type: 'error',
        title: 'No Data Available',
        message: 'Unable to retrieve forecast data. Please try refreshing.',
        id: 'no-data'
      });
    }
    
    // Check for API errors (if error state is set)
    if (error) {
      issues.push({
        type: 'error',
        title: 'API Error',
        message: String(error),
        id: 'api-error'
      });
    }
    
    // Extract errors from fog forecast response
    this.extractForecastErrors(timelineData, issues);

    // Sort by severity and return most severe issue if any exist
    if (issues.length > 0) {
      return this.getMostSevereIssue(issues);
    }
    
    return null;
  }
  
  /**
   * Extracts error information from forecast data
   */
  private static extractForecastErrors(
    timelineData: HourlyForecastTimelineData[],
    issues: NotificationIssue[]
  ): void {
    // Process each airport's timeline
    for (const timeline of timelineData) {
      // Check if there are any errors at the timeline level
      if ('errors' in timeline) {
        const timelineErrors = (timeline as ExtendedTimelineData).errors;
        if (timelineErrors && Array.isArray(timelineErrors) && timelineErrors.length > 0) {
          console.log(`Found ${timelineErrors.length} timeline-level errors for ${timeline.location_name}:`, timelineErrors);
          this.processErrorInfoObjects(timelineErrors, issues, timeline.location_name);
        }
      }
      
      for (const segment of timeline.segments) {
        // Check raw predictions for errors
        if (segment.raw_predictions && 'errors' in segment.raw_predictions) {
          const predictionErrors = (segment.raw_predictions as ExtendedAggregatedPredictions).errors;
          if (predictionErrors && Array.isArray(predictionErrors) && predictionErrors.length > 0) {
            console.log(`Found ${predictionErrors.length} prediction errors in segment at ${segment.time} for ${timeline.location_name}:`, predictionErrors);
            this.processErrorInfoObjects(predictionErrors, issues, timeline.location_name);
          }
        }
        
        // Check raw actuals for errors
        if (segment.raw_actuals && 'errors' in segment.raw_actuals) {
          const actualErrors = (segment.raw_actuals as ExtendedAggregatedActuals).errors;
          if (actualErrors && Array.isArray(actualErrors) && actualErrors.length > 0) {
            console.log(`Found ${actualErrors.length} actual errors in segment at ${segment.time} for ${timeline.location_name}:`, actualErrors);
            this.processErrorInfoObjects(actualErrors, issues, timeline.location_name);
          }
        }
      }
    }
  }
  
  /**
   * Error codes that should not be shown as notifications (handled visually in the timeline)
   */
  private static suppressedErrorCodes = ['OUT_OF_OPERATING_HOURS'];

  /**
   * Process ErrorInfo objects from the backend
   */
  private static processErrorInfoObjects(
    errors: ErrorInfo[],
    issues: NotificationIssue[],
    locationName: string
  ): void {
    // Log error info for debugging
    console.log(`Processing ${errors.length} error(s) for location ${locationName}:`, errors);

    for (const error of errors) {
      // Skip suppressed error codes (handled visually in the timeline)
      if (this.suppressedErrorCodes.includes(error.code)) {
        console.log(`Skipping suppressed error code ${error.code} for ${locationName}`);
        continue;
      }

      // Skip if we already have an identical error
      if (issues.some(issue => issue.id === `${error.code}-${locationName}`)) {
        continue;
      }

      // Use our mapping for user-friendly messages or fall back to original message
      const message = this.errorMessageMap[error.code] || error.message;
      const title = this.errorTitleMap[error.code] || error.code.replace(/_/g, ' ');

      console.log(`Creating notification for error ${error.code} at ${locationName}:`, {
        originalError: error,
        mappedTitle: title,
        mappedMessage: message
      });

      issues.push({
        type: error.severity as 'error' | 'warning', // Cast to match NotificationIssue type
        title: title,
        message: `${message} (${locationName})`,
        id: `${error.code}-${locationName}`
      });
    }
  }
  
  /**
   * Sorts issues by severity and returns the most severe one, aggregating locations with same error
   */
  private static getMostSevereIssue(issues: NotificationIssue[]): NotificationIssue {
    // Group location-specific issues by error code
    const locationSpecificErrors = new Map<string, { 
      issue: NotificationIssue, 
      locations: string[] 
    }>();
    const otherIssues: NotificationIssue[] = [];
    
    issues.forEach(issue => {
      // Check if this is a location-specific error (format: CODE-LocationName)
      // Only aggregate errors that match our known error codes
      const knownErrorCodes = [
        // NZ forecast errors
        'MISSING_PREDICTIONS', 'MISSING_ACTUALS', 'EXCESSIVE_MISSING_PREDICTIONS', 'EXCESSIVE_MISSING_ACTUALS',
        // UK METAR forecast errors
        'MISSING_PREDICTIONS_30MIN', 'MISSING_PREDICTIONS_HOURLY', 'EXCESSIVE_MISSING_PREDICTIONS_30MIN', 'EXCESSIVE_MISSING_PREDICTIONS_HOURLY'
      ];
      const codeMatch = issue.id.match(/^([^-]+)-(.+)$/);
      
      if (codeMatch && knownErrorCodes.includes(codeMatch[1])) {
        const errorCode = codeMatch[1];
        const location = codeMatch[2];
        
        if (locationSpecificErrors.has(errorCode)) {
          locationSpecificErrors.get(errorCode)!.locations.push(location);
        } else {
          // Create a new issue without the location in the message (we'll add all locations later)
          const baseMessage = issue.message.replace(/ \([^)]+\)$/, ''); // Remove location from message
          locationSpecificErrors.set(errorCode, {
            issue: { ...issue, message: baseMessage, id: errorCode },
            locations: [location]
          });
        }
      } else {
        // Non-location specific error or unknown format, add as-is
        otherIssues.push(issue);
      }
    });
    
    // Convert grouped location-specific issues back to array with aggregated locations
    const aggregatedIssues: NotificationIssue[] = [...otherIssues];
    
    locationSpecificErrors.forEach((group) => {
      // Add all locations to the message
      const locationString = group.locations.join(', ');
      aggregatedIssues.push({
        ...group.issue,
        message: `${group.issue.message} (${locationString})`
      });
    });
    
    // Sort by severity - errors first, then warnings, then info
    // Within same severity, prediction issues take precedence over actuals issues
    aggregatedIssues.sort((a, b) => {
      if (a.type === 'error' && b.type !== 'error') return -1;
      if (a.type !== 'error' && b.type === 'error') return 1;
      if (a.type === 'warning' && b.type === 'info') return -1;
      if (a.type === 'info' && b.type === 'warning') return 1;
      // Within same severity, prediction errors before actuals errors
      const aIsPrediction = a.id.toUpperCase().includes('PREDICTION');
      const bIsPrediction = b.id.toUpperCase().includes('PREDICTION');
      if (aIsPrediction && !bIsPrediction) return -1;
      if (!aIsPrediction && bIsPrediction) return 1;
      return 0;
    });
    
    return aggregatedIssues[0];
  }

  /**
   * Extracts error information directly from the forecast responses
   * This is useful for components that have access to the raw forecast responses
   */
  static extractErrorsFromForecast(
    forecasts: FogForecastResponse[],
    locationNames: Record<number, string>
  ): NotificationIssue | null {
    if (!forecasts || forecasts.length === 0) {
      return null;
    }

    const issues: NotificationIssue[] = [];

    // Process each forecast
    forecasts.forEach(forecast => {
      if (forecast.errors && forecast.errors.length > 0) {
        // Find the location name
        const locationId = forecast.aggregated_predictions[0]?.location || 
                          forecast.aggregated_actuals[0]?.location || 0;
        const locationName = locationNames[locationId] || 'Unknown';

        // Process the errors
        this.processErrorInfoObjects(forecast.errors, issues, locationName);
      }
    });

    // Return the most severe issue if any found
    if (issues.length > 0) {
      return this.getMostSevereIssue(issues);
    }

    return null;
  }
} 